import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { convertStringToCamelCase } from '../../../utils/stringUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import ModalContentAttendanceOutcome from '../attendanceOutcome/ModalContentAttendanceOutcome';
import ModalContentCTWReferral from '../ctwReferrals/ModalContentCTWReferral';
import ModalContentFinancialRequest from '../financialRequests/ModalContentFinancialRequest';
import ModalContentReferralsCreated from '../referralsCreated/ModalContentReferralsCreated';
import { validate } from '../validation/validateDataSets';

import '../../../commonStyles/muiStyles.css';
import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const presentRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

export const initialAttendanceOutcomeStatus = {
    contract: {},
    service: {},
    attendanceStatuses: [],
    date: String(new Date().toISOString().split('T')[0])
};
export const initialCTWReferralStatus = {
    contract: {},
    service: {},
    claimEventType: {},
    claimEventStatus: {}
};
export const initialFinancialRequestStatus = {
    contract: {},
    serviceId: '',
    teamIds: [],
    date: String(new Date().toISOString().split('T')[0])
};
export const initialReferralsCreatedStatus = { contract: {} };

const DataSetsModal = ({ dataSets, contracts, onCancel, isOpen }) => {
    const navigate = useNavigate();

    // LOCAL STATE
    const [selects, setSelects] = useState({});
    const [errors, setErrors] = useState({});
    const [selectedDataSet, setSelectedDataSet] = useState({});
    const [type, setType] = useState('');
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);

    // USE EFFECTS
    useEffect(() => {
        setIsSubmitDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (selectedDataSet && 'name' in selectedDataSet) {
            setType(convertStringToCamelCase(selectedDataSet.name || ''));
        } else {
            setType('');
        }
    }, [selectedDataSet]);

    useEffect(() => {
        setErrors({});
        if (!type) {
            setSelects({});
            return;
        }
        switch (type) {
            case 'attendanceOutcomes':
                setSelects({ ...initialAttendanceOutcomeStatus });
                break;
            case 'ctwReferral':
                setSelects({ ...initialCTWReferralStatus });
                break;
            case 'financialRequest':
                setSelects({ ...initialFinancialRequestStatus });
                break;
            case 'referralsCreated':
                setSelects({ ...initialReferralsCreatedStatus });
                break;
            default:
                return;
        }
    }, [type]);

    // FNS
    const clearFields = () => {
        setSelects({});
        setSelectedDataSet({});
        setType('');
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onChange = (key, value) => {
        clearError(key);
        setSelects((prev) => ({ ...prev, [key]: value }));
    };

    const onClose = () => {
        clearFields();
        onCancel();
    };

    const onPresentAttendanceOutcome = () => {
        const newErrors = validate('attendanceOutcomes', selects);
        setErrors(newErrors);
        if (Object.values(newErrors).some((el) => el.error)) return;
        onClose();
        navigate('/attendance_outcome', { state: { selects } });
    };

    const onPresentCTWReferrals = () => {
        const newErrors = validate('ctwReferral', selects);
        setErrors(newErrors);
        if (Object.values(newErrors).some((el) => el.error)) return;
        onClose();
        navigate('/ctw_referral', { state: { selects } });
    };

    const onPresentFinancialRequests = () => {
        const newErrors = validate('financialRequest', selects);
        setErrors(newErrors);
        if (Object.values(newErrors).some((el) => el.error)) return;
        onClose();
        navigate('/financial_requests', { state: { selects } });
    };

    const onPresentReferrals = () => {
        const newErrors = validate('referralsCreated', selects);
        setErrors(newErrors);
        if (Object.values(newErrors).some((el) => el.error)) return;
        onClose();
        navigate('/referrals_created', { state: { selects } });
    };

    const onPresent = () => {
        if (type === 'attendanceOutcomes') onPresentAttendanceOutcome();
        else if (type === 'ctwReferral') onPresentCTWReferrals();
        else if (type === 'financialRequest') onPresentFinancialRequests();
        else if (type === 'referralsCreated') onPresentReferrals();
    };

    // RENDER
    const renderModalContent = () => {
        if (!type) return null;
        switch (type) {
            case 'attendanceOutcomes':
                return (
                    <ModalContentAttendanceOutcome
                        contracts={contracts}
                        errors={errors}
                        selects={selects}
                        onChange={onChange}
                    />
                );
            case 'ctwReferral':
                return (
                    <ModalContentCTWReferral
                        contracts={contracts}
                        errors={errors}
                        selects={selects}
                        onChange={onChange}
                    />
                );
            case 'financialRequest':
                return (
                    <ModalContentFinancialRequest
                        contracts={contracts}
                        errors={errors}
                        selects={selects}
                        onChange={onChange}
                    />
                );
            case 'referralsCreated':
                return (
                    <ModalContentReferralsCreated
                        contracts={contracts}
                        errors={errors}
                        selects={selects}
                        onChange={onChange}
                    />
                );
            default:
                setType('');
                return;
        }
    };

    return (
        <Dialog open={isOpen} fullWidth={true} className="muiDialogWrapper">
            <div className={modal.modalHeader}>
                <DialogTitle>Data Set Modal</DialogTitle>
            </div>
            <DialogContent>
                <form className={form.formWrapper}>
                    <div className={form.formSection}>
                        <SingleSelect
                            id="dataSet"
                            label="Select Data Set"
                            placeholder="Select data set..."
                            mandatory={true}
                            customClass="absoluteAndHeightControlThirdLevel"
                            menuItems={dataSets || []}
                            selectedId={selectedDataSet?.id || ''}
                            selected={dataSets.find((el) => el.id === selectedDataSet?.id) || {}}
                            error={errors.dataSet}
                            onChange={(id) =>
                                setSelectedDataSet(dataSets.find((el) => el.id === id))
                            }
                        />
                    </div>
                    {renderModalContent()}
                </form>

                <div className={modal.actionButtonsContainer}>
                    <div className={modal.modalActions}>
                        {type && (
                            <div>
                                <Button
                                    id="presentDataSet"
                                    disabled={!hasRole(presentRoles, roles) || isSubmitDisabled}
                                    content="Present"
                                    onClick={onPresent}
                                />
                            </div>
                        )}
                    </div>
                    <div className={modal.cancelButton}>
                        <Button id="cancelDataSets" content="Cancel" onClick={onClose} />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

DataSetsModal.propTypes = {
    dataSets: PropTypes.arrayOf(PropTypes.object),
    contracts: PropTypes.arrayOf(PropTypes.object),
    onCancel: PropTypes.func,
    isOpen: PropTypes.bool
};

export default DataSetsModal;
